import { getAuth, type User as FirebaseUser } from 'firebase/auth';
import { useEffect } from 'react';
import { getUserMetadataByUserId } from '@/api/user';
import { useAuthenticatedUser } from '@/hooks/user/useAuthenticatedUser';
import i18n from '@/i18n';
import { getI18nResourceByLanguage } from '@/i18n/i18nLanguageResource';

export const useFirebaseIdTokenChangedListener = () => {
  const { data: edsyUser, mutate } = useAuthenticatedUser();

  useEffect(() => {
    const auth = getAuth();

    const unsubscribe = auth.onIdTokenChanged(async (firebaseUser: FirebaseUser | null) => {
      // firebase is logged-in but edsy user is not fetched (needs update)
      if (firebaseUser && !edsyUser) {
        if (!localStorage.getItem('loggedInOnce')) {
          try {
            const { language } = await getUserMetadataByUserId(firebaseUser.uid);

            const i18nResource = getI18nResourceByLanguage(language);
            i18n.changeLanguage(i18nResource.defaultResource);
            // Latched only on success: a failed lookup (e.g. a Google credential with no Edsy
            // account yet) must not burn the one-shot, or the next real login never restores
            // the user's language.
            localStorage.setItem('loggedInOnce', 'true');
          } catch {
            // Keep whatever language the user is already reading — the metadata is unknown,
            // not "Thai".
          }
        }
        await mutate();
      }
      // firebase is logged-out but edsy user is still present (stale)
      else if (!firebaseUser && edsyUser) {
        await mutate();
      }
    });

    return () => unsubscribe();
  }, [edsyUser, mutate]);
};
